<template>
  <div class="display-connect">
    <InlineVideoPlayer
      v-if="isInlineVideo"
      ref="inlineVideoPlayerRef"
      :has-next="false"
      muted
      hide-controls
      @close="onClose"
    />
    <p class="display-title" v-text="displayTitle" />
    <div class="display-play-status flex-column flex-center">
      <p v-if="!currentExperience" class="status-header" v-text="t('nothingPlaying')" />
      <p v-else-if="audioPlayState.canplay" class="status-header" v-text="t('currentlyPlaying')" />
      <FontAwesomeIcon
        v-else
        class="status-spinner"
        icon="fa-spinner"
        size="3x"
      />
      <p v-if="statusTitle" class="status-text" v-html="statusTitle" />
      <div class="status-ring" :class="{ 'status-ring--active': !!currentExperience }" />
    </div>
    <button
      data-test="connect-btn"
      class="connect-btn btn"
      @click="onDisconnect"
      v-text="t('disconnect')"
    />
    <SouvenirItem v-if="exhibit?.souvenirId" :exhibit="exhibit" />
  </div>
</template>

<script lang="ts">
import { DeepReadonly, PropType, computed, defineComponent, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import InlineVideoPlayer from '@/components/InlineVideoPlayer.vue'
import SouvenirItem from '@/components/SouvenirItem.vue'
import { useDisplayConnect } from '@/composables/display-connect'
import { useExperienceStore } from '@/stores/experience'

import type { Exhibit } from 'types/data'

export default defineComponent({
  components: {
    InlineVideoPlayer,
    SouvenirItem,
  },

  emits: {
    disconnect: (): true => true,
  },

  props: {
    exhibit: { type: [Object, null] as PropType<DeepReadonly<Exhibit> | null>, required: true },
  },

  setup (props, { emit }) {
    const { t } = useI18n()

    const inlineVideoPlayerRef = ref<typeof InlineVideoPlayer | null>(null)
    const { audioPlayState, currentExperience } = useDisplayConnect({
      exhibit: computed(() => props.exhibit),
      onResync: async (time) => {
        await nextTick()
        inlineVideoPlayerRef.value?.resync(time)
      },
    })

    const displayTitle = computed(() => {
      return props.exhibit?.name ?? ''
    })

    const statusTitle = computed<string | null>(() => {
      if (!currentExperience.value) return t('connectToDisplayToHear')
      if ('description' in currentExperience.value && currentExperience.value.description) return currentExperience.value.description
      if ('name' in currentExperience.value && currentExperience.value.name) return currentExperience.value.name
      return null
    })

    function onDisconnect (): void {
      emit('disconnect')
    }

    // inline video
    const isInlineVideo = computed(() => {
      if (!currentExperience.value) return false
      return 'type' in currentExperience.value && currentExperience.value.type === 'inlineVideo'
    })
    const experienceStore = useExperienceStore()
    function onClose (): void {
      experienceStore.deactivate()
      onDisconnect()
    }

    return {
      t,

      displayTitle,

      currentExperience,
      audioPlayState,
      statusTitle,

      onDisconnect,

      // inline video
      isInlineVideo,
      onClose,
      inlineVideoPlayerRef,
    }
  },
})
</script>

<style lang="scss" scoped>
.display-connect {
  position: fixed;
  background-color: var(--midgray);
  color: var(--darkgray);
  top: var(--nav-height);
  bottom: 0;
  left: 0;
  right: 0;
  z-index: var(--z-display-connect);
  padding: 30px;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: space-between;
  text-align: center;
}

.display-title {
  font-size: 16px;
  font-family: 'JW Knoll Display';
  padding: 0 8px;
}

.display-play-status {
  position: relative;
  width: 280px;
  height: 150px;
  justify-content: center;
  top: -50px;
  .status-header {
    font-size: 30px;
    color: var(--primary);
    font-family: 'JW Knoll Demi';
  }
  .status-spinner {
    color: var(--primary);
    margin-bottom: 14px;
  }
  .status-text {
    margin-top: 22px;
    font-size: 18px;
  }
  .status-ring {
    position: absolute;
    border-radius: 50%;
    border: 20px solid var(--fadedgray);
    width: 200px;
    height: 200px;
    z-index: -1;
    opacity: 0.1;
    transition: all 0.5s ease-in-out;
    &.status-ring--active {
      border-color: var(--primary);
      width: 300px;
      height: 300px;
    }
  }
}

button {
  width: 330px;
}
</style>
